import React, { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Camera, Edit, Save, X, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PasswordInput } from '../../components/ui/password-input';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { compressImageSafe, uploadErrorMessage } from '../../utils/imageCompression';

import { getBackendUrl } from '../../utils/getBackendUrl';
import MyBadgesWidget from '../../components/MyBadgesWidget';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const AdminProfile = ({ user, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
    address: user.address || ''
  });

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/profile`, formData);
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleUploadPicture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Compress image before upload (512x512 for profile pictures)
      toast.info('Compressing image...');
      const compressedFile = await compressImageSafe(file, 512, 512, 0.85);
      
      const formData = new FormData();
      formData.append('file', compressedFile);

      await axios.post(`${API}/users/upload-profile-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile picture updated!');
      onUpdate();
    } catch (error) {
      toast.error(uploadErrorMessage(error));
      console.error('Upload error:', error);
    }
  };


  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post(`${API}/users/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully!');
      setIsChangingPassword(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <Shield className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Administrator Profile</h2>
            <p className="text-slate-400">Manage system and user settings</p>
          </div>
        </div>
      </div>

      {/* Badges */}
      <MyBadgesWidget />

      {/* Profile Info */}
      <div className="glass-card p-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Avatar Section */}
          <div className="flex flex-col items-center">
            <div className="relative mb-4">
              <Avatar className="w-32 h-32">
                <AvatarImage 
                  src={user.profile_picture ? `${BACKEND_URL}${user.profile_picture}` : undefined}
                  onError={(e) => {
                    console.error('[AdminProfile] Profile picture failed to load:', {
                      profile_picture: user.profile_picture,
                      full_url: user.profile_picture ? `${BACKEND_URL}${user.profile_picture}` : 'undefined',
                      user_id: user.id
                    });
                  }}
                  onLoad={() => {
                    console.log('[AdminProfile] Profile picture loaded successfully:', user.profile_picture);
                  }}
                />
                <AvatarFallback className="bg-orange-500 text-white text-3xl">
                  {user.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <label
                htmlFor="admin-profile-picture"
                className="absolute bottom-0 right-0 bg-orange-500 p-2 rounded-full cursor-pointer hover:bg-orange-600 transition-colors"
                data-testid="upload-picture-btn"
              >
                <Camera className="w-4 h-4 text-white upload-icon-idle" />
                <input
                  id="admin-profile-picture"
                  type="file"
                  accept="image/*"
                  onChange={handleUploadPicture}
                  className="hidden"
                />
              </label>
            </div>
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-1">{user.name}</h2>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-sm font-semibold">
                <Shield className="w-4 h-4" />
                Administrator
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="flex-1">
            {isEditing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4" data-testid="edit-profile-form">
                <div>
                  <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-slate-300">Phone Number</Label>
                  <Input
                    id="phone"
                    data-testid="phone-input"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="address" className="text-slate-300">Address</Label>
                  <Input
                    id="address"
                    data-testid="address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" className="bg-gradient-to-r from-orange-500 to-orange-600" data-testid="save-profile-btn">
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                    data-testid="cancel-edit-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-slate-400 text-sm mb-1">Email</p>
                  <p className="text-white font-semibold">{user.email}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-1">Phone</p>
                  <p className="text-white font-semibold">{user.phone || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm mb-1">Address</p>
                  <p className="text-white font-semibold">{user.address || 'Not provided'}</p>
                </div>
                <Button
                  onClick={() => setIsEditing(true)}
                  className="bg-gradient-to-r from-orange-500 to-orange-600"
                  data-testid="edit-profile-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Admin Capabilities */}
      <div className="glass-card p-6">
        <h3 className="text-xl font-bold text-white mb-4">Administrator Capabilities</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-slate-800/50 rounded-lg">
            <p className="text-white font-semibold mb-1">✓ Full System Access</p>
            <p className="text-slate-400 text-sm">Manage all projects, users, and settings</p>
          </div>
          <div className="p-4 bg-slate-800/50 rounded-lg">
            <p className="text-white font-semibold mb-1">✓ User Management</p>
            <p className="text-slate-400 text-sm">Create, edit, and remove user accounts</p>
          </div>
          <div className="p-4 bg-slate-800/50 rounded-lg">
            <p className="text-white font-semibold mb-1">✓ Site Customization</p>
            <p className="text-slate-400 text-sm">Customize branding and site settings</p>
          </div>
          <div className="p-4 bg-slate-800/50 rounded-lg">
            <p className="text-white font-semibold mb-1">✓ Reports & Analytics</p>
            <p className="text-slate-400 text-sm">View comprehensive project reports</p>
          </div>
        </div>
      </div>


      {/* Password Change Section */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-500/20 rounded-lg">
            <Lock className="w-5 h-5 text-red-400" />
          </div>
          <h3 className="text-xl font-bold text-white">Change Password</h3>
        </div>

        {!isChangingPassword ? (
          <Button
            onClick={() => setIsChangingPassword(true)}
            className="bg-gradient-to-r from-red-500 to-red-600"
          >
            <Lock className="w-4 h-4 mr-2" />
            Change Password
          </Button>
        ) : (
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="current_password" className="text-slate-300">Current Password</Label>
              <PasswordInput
                id="current_password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="new_password" className="text-slate-300">New Password</Label>
              <PasswordInput
                id="new_password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength={6}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="confirm_password" className="text-slate-300">Confirm New Password</Label>
              <PasswordInput
                id="confirm_password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                minLength={6}
                className="mt-1"
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="bg-gradient-to-r from-red-500 to-red-600">
                <Save className="w-4 h-4 mr-2" />
                Update Password
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsChangingPassword(false);
                  setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                }}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>

    </div>
  );
};

export default AdminProfile;